import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "api-authentication-headers"
    }}>{`API authentication headers`}</h1>
    <InlineAlert variant="success" slots="text" mdxType="InlineAlert" />
TL;DR: All Stock API requests require two basic headers, and authenticated requests require you to add an access token. Here you can learn the format for these headers and test them.
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`In the previous step, you registered your application on the `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/console"
      }}>{`Adobe Developer Console`}</a>{`. Now you will take the credentials created for you, and use them to build proper headers.`}</p>
    <p>{`Note that these topics are explored in detail in each of these workflow guides, so only the summary of the process will be discussed here:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "07-workflow-guides.md"
        }}>{`Service account workflow`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "07-workflow-guides.md"
        }}>{`OAuth workflow`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "07-workflow-guides.md"
        }}>{`Affiliate (API key) workflow`}</a></li>
    </ul>
    <h2 {...{
      "id": "store-your-credentials"
    }}>{`Store your credentials`}</h2>
    <p>{`The first thing you will need is the information given to you when you registered your application. Depending on which integration method you chose, you will only need particular fields.`}</p>
    <p>{`Other than the API key, the information here--especially the client secret--should be safeguarded like any sensitive credentials, similar to your application's private key. At a minimum, this data should be stored on your server in an file inaccessible to the public. At no time should it be exposed in front-end JavaScript, for example.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Integration type`}</strong></th>
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Fields required`}</strong></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`API Key`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`API key (aka, Client ID)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`OAuth`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`API key, client secret, redirect URI`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Service Account`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`API key, technical account ID, organization ID, client secret`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "create-your-basic-header"
    }}>{`Create your basic header`}</h2>
    <p>{`The Adobe Stock APIs is a REST-based service. Subsequently, all requests to the Stock APIs require these HTTP headers:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`x-api-key`}</strong>{`: Your API key`}</li>
      <li parentName="ul"><strong parentName="li">{`X-Product`}</strong>{`: Your application's name`}</li>
    </ul>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`    X-Product: MySampleApp/1.0
    X-API-Key: abc123c9f1194eac8a63cc25c1b9b9df
`}</code></pre>
    <p>{`The format for X-Product can be any string, however a common convention is to include the version number separated by a slash.`}</p>
    <h3 {...{
      "id": "test-your-api-key-header"
    }}>{`Test your API key header`}</h3>
    <p>{`For unauthenticated requests such as normal search queries, this is all you need, and you can start building your application. But before you do, run a quick test to make sure it is working. Simply replace "YourApiKeyHere" with the key you generated, and when you run this command, you should get back JSON results of cats wearing costumes, proving that it works.`}</p>
    <p>{`Example search query using `}<a parentName="p" {...{
        "href": "https://curl.haxx.se/"
      }}>{`curl`}</a>{`. Note that line breaks have been added for clarity.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`curl "https://stock.adobe.io/Rest/Media/1/Search/Files?locale=en_US
    &search_parameters%5Bwords%5D=cats%20in%20costume" 
    -H "x-api-key: YourApiKeyHere" 
    -H "x-product: MySampleApp/1.0"
`}</code></pre>
    <h2 {...{
      "id": "generate-an-access-token"
    }}>{`Generate an access token`}</h2>
    <p>{`All License API requests to Adobe Stock must be authenticated and authorized using an access token string which follows the JSON Web Token (JWT) standard. The token will contain claims authorizing the bearer of the token access to protected resources (such as licensed images), and is digitally signed by the issuer. All access tokens you will use in your workflow must be issued by the Adobe Identity Management Services (IMS). In the Service Account workflow, this token will be generated as a result of exchanging tokens between your application and Adobe IMS, and for the OAuth workflow, Adobe IMS will issue the token as the result of a successful login.`}</p>
    <p>{`The creation of tokens is beyond the scope of this article, however it is covered in depth in the Service Account workflow and OAuth workflow guides.`}</p>
    <p>{`For complete information about creating access tokens, see the Adobe I/O `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/developer-console/docs/guides/authentication/"
      }}>{`API Authentication Guide`}</a>{`. Here you will also find examples of creating JWTs using Java and Node.js.`}</p>
    <a id="important-use-the-correct-scope-in-your-jwt"></a>
    <h3 {...{
      "id": "important-use-the-correct-scope-in-your-jwt"
    }}>{`Important: Use the correct scope in your JWT`}</h3>
    <p>{`Please note that if you are following the Service Account workflow, the Adobe I/O API Authentication Guide (above) uses a different scope in its sample JWT payload than the one required by the Stock API. The correct scope for your application is listed in the `}<strong parentName="p">{`JWT`}</strong>{` tab of the Adobe Developer Console, and it will include your proper payload.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "672px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "50%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/stock/docs/static/18fad50ac74eafccb992919a5767826c/5530d/io_jwt-tab.webp 320w", "/stock/docs/static/18fad50ac74eafccb992919a5767826c/0c8fb/io_jwt-tab.webp 640w", "/stock/docs/static/18fad50ac74eafccb992919a5767826c/42a33/io_jwt-tab.webp 672w"],
            "sizes": "(max-width: 672px) 100vw, 672px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/stock/docs/static/18fad50ac74eafccb992919a5767826c/dd4a7/io_jwt-tab.png 320w", "/stock/docs/static/18fad50ac74eafccb992919a5767826c/0f09e/io_jwt-tab.png 640w", "/stock/docs/static/18fad50ac74eafccb992919a5767826c/00e42/io_jwt-tab.png 672w"],
            "sizes": "(max-width: 672px) 100vw, 672px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/stock/docs/static/18fad50ac74eafccb992919a5767826c/00e42/io_jwt-tab.png",
            "alt": "JWT tab of Service Account integration",
            "title": "JWT tab of Service Account integration",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "add-token-to-header"
    }}>{`Add token to header`}</h2>
    <p>{`Once your token is generated, add it to your authenticated request using this syntax:`}</p>
    <pre><code parentName="pre" {...{}}>{`    Authorization:Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiYWRtaW4iOnRydWV9.TJVA95OrM7E2cBab30RMHrHDcEfxjoYZgeFONFh7HgQ
`}</code></pre>
    <p>{`The sample token above comes from `}<a parentName="p" {...{
        "href": "https://jwt.io/"
      }}>{`jwt.io`}</a>{`, an excellent resource for learning about and troubleshooting tokens.`}</p>
    <h3 {...{
      "id": "test-your-access-token"
    }}>{`Test your access token`}</h3>
    <p>{`Like we tested our API key header, now we can test our access token by combining it with the earlier headers (which are still required), and calling an API that requires authentication. The Member/Profile request is perfect for this test, as it will tell you what Adobe Stock entitlements you have (if any).`}</p>
    <p>{`Replace the text "PutYourAccessToken" with the token you generated, and replace "YourApiKeyHere" with your key, and run from a terminal prompt.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`  curl "https://stock.adobe.io/Rest/Libraries/1/Member/Profile?locale=en_US" -H "authorization: Bearer PutYourAccessToken" -H "x-api-key: YourApiKeyHere" -H "x-product: MySampleApp/1.0"
`}</code></pre>
    <p>{`If you correctly generated an access token, you should see something like this (JSON has been formatted for display).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`    {
        "available_entitlement": {
            "quota": 49,
            "license_type_id": 1,
            "has_credit_model": false,
            "has_agency_model": false,
            "is_cce": false,
            "full_entitlement_quota": {
                "image_quota": 49
            }
        },
        "member": {
            "stock_id": 1272100
        },
        "purchase_options": {
            "state": "possible",
            "requires_checkout": false,
            "message": "This will use 1 of your 49 licenses."
        }
    }
`}</code></pre>
    <p>{`Even if your image quota is 0, congratulations for getting this far! Generating the token is the most difficult part of the Stock API process.`}</p>
    <p><strong parentName="p">{`>`}{`>`}{`>`}{` NEXT:`}</strong>{` Learn the Stock API command structure and  `}<a parentName="p" {...{
        "href": "./04-creating-apps.md"
      }}>{`start building your app`}</a>{`!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      